import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { ApiError, getMe, listProducts, logout } from '../lib/api-client';
import { clearAccessToken } from '../lib/auth';
import { formatPrice, pickI18n } from '../lib/format';
import { useI18n } from '../lib/i18n-context';
import { clearOrderHistory } from '../lib/order-history';
import { clearRefundHistory } from '../lib/refund-history';
import { htmlToPlainText } from '../lib/sanitize-html';
import { clearTicketHistory, listTicketHistory } from '../lib/ticket-history';

export function HomePage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t, locale } = useI18n();

  // 商品搜尋 / 分類篩選（純 client-side；listProducts 已回全量）
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');

  const userQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: getMe,
    retry: false,
  });

  const productsQuery = useQuery({
    queryKey: ['products'],
    queryFn: listProducts,
    staleTime: 60_000,
  });

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSettled: () => {
      const uid = userQuery.data?.user_id;
      clearTicketHistory(uid);
      clearRefundHistory(uid);
      clearOrderHistory(uid);
      clearAccessToken();
      queryClient.clear();
      navigate('/login', { replace: true });
    },
  });

  // 我的票券（local history，FE-only — orders 表故意無 user_id；見 ticket-history.ts 註）
  const ticketHistory = useMemo(
    () => listTicketHistory(userQuery.data?.user_id),
    [userQuery.data?.user_id],
  );

  const allItems = productsQuery.data?.items ?? [];
  // 分類清單由商品資料推導（去重 + 排序）
  const categories = useMemo(
    () => Array.from(new Set(allItems.map((p) => p.category))).sort(),
    [allItems],
  );
  // 套用搜尋（名稱多語 + SKU，不分大小寫）與分類過濾
  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allItems.filter((p) => {
      if (category !== '' && p.category !== category) return false;
      if (q === '') return true;
      const name = pickI18n(p.name_i18n, p.sku, locale).toLowerCase();
      return name.includes(q) || p.sku.toLowerCase().includes(q);
    });
  }, [allItems, search, category, locale]);

  return (
    <div className="app-shell">
      <header className="topbar">
        <h1>{t('home.title')}</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <LanguageSwitcher />
          <button
            type="button"
            className="btn-secondary"
            onClick={() => logoutMutation.mutate()}
            disabled={logoutMutation.isPending}
          >
            {logoutMutation.isPending ? t('common.logoutPending') : t('common.logout')}
          </button>
        </div>
      </header>

      <nav style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Link
          to="/recommendations"
          className="btn-secondary"
          data-testid="home-nav-recommend"
          style={{ textDecoration: 'none', fontSize: 14, borderRadius: 999 }}
        >
          {t('home.navRecommend')}
        </Link>
        <Link
          to="/me/orders"
          className="btn-secondary"
          style={{ textDecoration: 'none', fontSize: 14, borderRadius: 999 }}
        >
          {t('home.navMyOrders')}
        </Link>
        <Link
          to="/me/refunds"
          className="btn-secondary"
          style={{ textDecoration: 'none', fontSize: 14, borderRadius: 999 }}
        >
          {t('home.navMyRefunds')}
        </Link>
        <Link
          to="/me/account"
          className="btn-secondary"
          style={{ textDecoration: 'none', fontSize: 14, borderRadius: 999 }}
        >
          {t('home.navAccount')}
        </Link>
      </nav>

      {ticketHistory.length > 0 && (
        <section style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 18, marginBottom: 8 }}>
            {t('home.myTicketsHeading', { count: ticketHistory.length })}
          </h2>
          <article className="card" style={{ padding: 0 }}>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {ticketHistory.slice(0, 10).map((tk) => (
                <li
                  key={tk.ticketId}
                  style={{
                    padding: '10px 14px',
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <Link
                      to={`/tickets/${tk.ticketId}`}
                      style={{ fontFamily: 'monospace', fontSize: 14 }}
                    >
                      {tk.ticketCode ?? `${tk.ticketId.slice(0, 8)}…`}
                    </Link>
                    {tk.orderId !== undefined && (
                      <span style={{ marginLeft: 8, fontSize: 12, color: '#777' }}>
                        {t('home.orderRef', { id: `${tk.orderId.slice(0, 8)}…` })}
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: 12, color: '#999' }}>
                    {new Date(tk.addedAt).toLocaleDateString()}
                  </span>
                </li>
              ))}
            </ul>
          </article>
          {ticketHistory.length > 10 && (
            <p style={{ fontSize: 12, color: '#777', marginTop: 4 }}>{t('home.ticketsCapNote')}</p>
          )}
        </section>
      )}

      {productsQuery.isSuccess && allItems.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('home.searchPlaceholder')}
            style={{ flex: '1 1 180px', padding: '6px 10px', fontSize: 14 }}
          />
          {categories.length > 1 && (
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={{ padding: '6px 10px', fontSize: 14 }}
            >
              <option value="">{t('home.allCategories')}</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {productsQuery.isPending && <p>{t('common.loading')}</p>}
      {productsQuery.isError && (
        <p className="error-text">
          {t('home.loadFailed')}
          {productsQuery.error instanceof ApiError
            ? (productsQuery.error.problem?.detail ?? productsQuery.error.message)
            : t('common.unknownError')}
        </p>
      )}
      {productsQuery.isSuccess && (
        <section>
          {allItems.length === 0 ? (
            <p>{t('home.empty')}</p>
          ) : filteredProducts.length === 0 ? (
            <p style={{ color: '#777' }}>{t('home.noMatch')}</p>
          ) : (
            filteredProducts.map((p) => {
              const name = pickI18n(p.name_i18n, p.sku, locale);
              // 列表卡片用去標籤純文字摘要（描述可能為 HTML，避免清單/段落破版）
              const desc = htmlToPlainText(pickI18n(p.description_i18n, '', locale));
              return (
                <article key={p.id} className="card">
                  <h3 style={{ margin: '0 0 4px' }}>{name}</h3>
                  {desc !== '' && <p style={{ margin: '0 0 8px', color: '#555' }}>{desc}</p>}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: 8,
                    }}
                  >
                    <div>
                      <strong>{formatPrice(p.base_price, p.currency, locale)}</strong>
                      {p.duration_minutes !== null && (
                        <span style={{ marginLeft: 8, color: '#777' }}>
                          {t('home.durationMin', { minutes: p.duration_minutes })}
                        </span>
                      )}
                      {!p.is_published && (
                        <span style={{ marginLeft: 8, color: '#D93025' }}>
                          {t('home.unpublished')}
                        </span>
                      )}
                    </div>
                    {p.is_published && (
                      <Link
                        to={`/products/${p.id}`}
                        className="btn-primary"
                        style={{ textDecoration: 'none', fontSize: 14, padding: '8px 14px' }}
                      >
                        {t('home.buyNow')}
                      </Link>
                    )}
                  </div>
                </article>
              );
            })
          )}
        </section>
      )}
    </div>
  );
}
